import {
  TierInfo,
  UserTierTypes,
} from '@eCommerce/frontend-legacy-shared-library-native';

/**
 * Local copy of the shared library's getTierProgress. Keep in sync until the
 * library version carrying it is published and pinned here.
 */

export interface GetTierProgressArgs {
  tierCredits: number;
  currentLevel?: UserTierTypes;
  currentTierInfo?: TierInfo;
  nextTierInfo?: TierInfo;
  allTiers?: TierInfo[];
}

export interface TierProgress {
  /** Fill from 0 to 1, measured from 0 credits to the target minimum. */
  percentage: number;
  remainingCredits: number;
  targetName: string;
  byInvitation: boolean;
  hideLabel: boolean;
  invitationRequired: boolean;
}

/**
 * Only consulted when a tier omits isEarnable. Olympus is the one tier no
 * balance can reach; Diamond Plus and Diamond Elite are earned on credits.
 */
const NON_EARNABLE_DEFAULTS = new Set<string>([UserTierTypes.OLYMPUS]);

const INVITATION_DEFAULTS = new Set<string>([
  UserTierTypes.SEVEN_STARS,
  UserTierTypes.OLYMPUS,
]);

const clamp01 = (value: number) => Math.max(0, Math.min(1, value));

const emptyProgress = (): TierProgress => ({
  percentage: 0,
  remainingCredits: 0,
  targetName: '',
  byInvitation: false,
  hideLabel: false,
  invitationRequired: false,
});

export const isEarnableTier = (tier: TierInfo): boolean => {
  if (typeof tier.isEarnable === 'boolean') {
    return tier.isEarnable;
  }
  return !NON_EARNABLE_DEFAULTS.has(tier.guestprofiletiercode);
};

export const isInvitationTier = (tier: TierInfo): boolean => {
  if (typeof tier.byInvitation === 'boolean') {
    return tier.byInvitation;
  }
  return INVITATION_DEFAULTS.has(tier.guestprofiletiercode);
};

/**
 * Progress runs from 0 credits to the minimum of the next earnable tier, and to Olympus once
 * the earnable ladder is exhausted. Every band is chosen on credits rather than on the displayed
 * tier, so a Diamond whose credits reset on 1/1 progresses toward Platinum again.
 */
export const getTierProgress = ({
  tierCredits,
  currentLevel,
  currentTierInfo,
  nextTierInfo,
  allTiers,
}: GetTierProgressArgs): TierProgress => {
  if (!currentLevel || !currentTierInfo) {
    return emptyProgress();
  }

  // Never name Olympus, even for a member who holds it: the tier is
  // unannounced, so no caller should be able to render it from this result.
  if (currentLevel === UserTierTypes.OLYMPUS) {
    return {...emptyProgress(), percentage: 1, hideLabel: true};
  }

  const credits = Math.max(0, tierCredits);

  // Past Seven Stars' minimum there is no earnable rung left, so progress runs to Olympus,
  // which requires an invitation at every balance in this band. Keyed on credits rather than
  // on the displayed tier: a member whose tier has not caught up to their balance — a Gold
  // sitting on 165,000 — otherwise exhausts the ladder below and dead-ends with
  // `invitationRequired` set but no tier left to name.
  const sevenStars = allTiers?.find(
    tier => tier.guestprofiletiercode === UserTierTypes.SEVEN_STARS
  );
  const olympus = allTiers?.find(
    tier => tier.guestprofiletiercode === UserTierTypes.OLYMPUS
  );
  const sevenStarsMin = sevenStars?.mincredits ?? 0;
  const olympusMin = olympus?.mincredits ?? 0;

  if (sevenStarsMin > 0 && credits >= sevenStarsMin) {
    return {
      ...emptyProgress(),
      percentage: olympusMin > 0 ? clamp01(credits / olympusMin) : 1,
      remainingCredits: Math.max(0, olympusMin - credits),
      targetName: olympus?.name ?? '',
      byInvitation: true,
      invitationRequired: true,
    };
  }

  const ladder =
    allTiers && allTiers.length > 0
      ? allTiers
      : ([currentTierInfo, nextTierInfo].filter(Boolean) as TierInfo[]);

  const earnable = ladder
    .filter(isEarnableTier)
    .slice()
    .sort((a, b) => a.mincredits - b.mincredits);

  const maxEarnable = earnable[earnable.length - 1];
  const nextEarnable = earnable.find(tier => credits < (tier.mincredits || 0));

  if (!nextEarnable) {
    const notYetEarnedMax =
      !!maxEarnable &&
      (currentTierInfo.mincredits || 0) < (maxEarnable.mincredits || 0);
    return {
      ...emptyProgress(),
      percentage: 1,
      hideLabel: !notYetEarnedMax,
      invitationRequired: notYetEarnedMax,
    };
  }

  const target = nextEarnable.mincredits || 0;
  return {
    ...emptyProgress(),
    percentage: target > 0 ? clamp01(credits / target) : 0,
    remainingCredits: Math.max(0, target - credits),
    targetName: nextEarnable.name || '',
    byInvitation: isInvitationTier(nextEarnable),
  };
};
